import { useCallback, useEffect, useRef, useState } from 'react';
import { isTyping, useShell } from '@/components/layout/shell';

/**
 * What a row spreads onto whatever element is its focus target.
 *
 * A roving tabindex: exactly one row in a list is tabbable, so Tab moves past a
 * forty-row list in one press and the keys move inside it. That is the ARIA
 * pattern for a list of choices, and it is also simply what a person expects
 * after using any other console.
 */
export interface RowProps {
  'data-row-id': string;
  tabIndex: number;
  onFocus: () => void;
}

function rowElement(id: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-row-id="${CSS.escape(id)}"]`);
}

/** The id of the row that has the focus, or null if the focus is elsewhere. */
function focusedRowId(): string | null {
  const active = document.activeElement as HTMLElement | null;
  return active?.dataset.rowId ?? null;
}

/**
 * `j` and `k` through a list, Enter to open what is under the ring.
 *
 * The console's keyboard story stopped at the palette: every row was reachable
 * only by Tab, and the lists a person lives in are the three the palette is for
 * getting *away* from. So the lists move under `j`/`k` (and the arrows, once you
 * are already in one -- taking ArrowDown from a page nobody has focused would
 * steal the scroll), and the row under the ring opens on Enter.
 *
 * Nothing fires inside a field, under the palette or the nav sheet, while the
 * `g` chord is armed, or with a modifier held: a shortcut that eats a keystroke
 * somebody meant for something else costs more than it saves.
 *
 * `ids` is the list in the order it is drawn -- across groups, since five panels
 * of rows are one list to the eye. Rows are found in the document by their id
 * rather than through a container ref, so a list split over those five panels
 * needs no wrapper it would not otherwise have.
 */
export function useRowKeys({
  ids,
  enabled = true,
  onEnter,
  onVerb,
}: {
  ids: string[];
  /** False while something modal is over the list. */
  enabled?: boolean;
  /** Set only where the row's own Enter would do something else. */
  onEnter?: (id: string) => void;
  /** `e` and `x`, where the list's rows have verbs of their own. */
  onVerb?: (verb: 'primary' | 'secondary', id: string) => void;
}): { rowProps: (id: string) => RowProps } {
  const { paletteOpen, navOpen, chordArmed } = useShell();
  const [preferred, setPreferred] = useState<string | null>(null);

  /**
   * The ring cannot sit on a row that is gone, and acting on a gate is exactly
   * what makes it go: `e` on the oldest one approves it and the list is a row
   * shorter a moment later. So the ring falls to whatever is at the same place
   * in the list now, which is the next gate down, and the person who was working
   * the list from the keyboard is still in it.
   *
   * Where it was is a ref rather than state: it is not what the component draws,
   * it is where to look when what it drew has gone.
   */
  const index = preferred ? ids.indexOf(preferred) : -1;
  const lastIndex = useRef(0);
  useEffect(() => {
    if (index >= 0) lastIndex.current = index;
  }, [index]);

  const fallback = ids.length ? (ids[Math.min(lastIndex.current, ids.length - 1)] ?? null) : null;
  const activeId = index >= 0 ? preferred : fallback;

  /**
   * ...and the ring is only worth moving if it was on screen to begin with. The
   * focus is nowhere exactly when the row holding it was removed, so that is the
   * one case this takes it back: a person typing in the reason field the verb
   * opened, or reading the detail the row linked to, keeps their place.
   */
  useEffect(() => {
    if (!preferred || index >= 0 || !fallback) return;
    // "Nowhere" is the body, the document, or nothing at all, depending on the
    // browser and on how the element holding the focus left.
    const active = document.activeElement;
    if (active && active !== document.body && active !== document.documentElement) return;
    setPreferred(fallback);
    rowElement(fallback)?.focus();
  }, [fallback, index, preferred]);

  const latest = useRef({ ids, onEnter, onVerb, activeId });
  useEffect(() => {
    latest.current = { ids, onEnter, onVerb, activeId };
  });

  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (paletteOpen || navOpen || chordArmed) return;
      if (event.metaKey || event.ctrlKey || event.altKey || isTyping(event.target)) return;

      const { ids: rows, onEnter: enter, onVerb: verb, activeId: current } = latest.current;
      if (!rows.length) return;

      const inList = focusedRowId() !== null;
      const index = current ? rows.indexOf(current) : -1;

      const move = (next: number) => {
        const id = rows[Math.max(0, Math.min(rows.length - 1, next))];
        if (!id) return;
        event.preventDefault();
        setPreferred(id);
        const element = rowElement(id);
        element?.focus();
        // A sticky group head can sit over the row focus just scrolled to.
        element?.scrollIntoView({ block: 'nearest' });
      };

      switch (event.key) {
        case 'j':
          return move(index + 1);
        case 'k':
          // From outside the list, either key goes to the top of it.
          return move(index < 0 ? 0 : index - 1);
        case 'ArrowDown':
          if (inList) move(index + 1);
          return;
        case 'ArrowUp':
          if (inList) move(index - 1);
          return;
        case 'Enter': {
          const id = rows[index];
          if (!enter || !inList || !id) return;
          event.preventDefault();
          enter(id);
          return;
        }
        case 'e':
        case 'x': {
          const id = rows[index];
          if (!verb || !inList || !id) return;
          event.preventDefault();
          verb(event.key === 'e' ? 'primary' : 'secondary', id);
          return;
        }
        default:
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [chordArmed, enabled, navOpen, paletteOpen]);

  const rowProps = useCallback(
    (id: string): RowProps => ({
      'data-row-id': id,
      tabIndex: activeId === id ? 0 : -1,
      onFocus: () => setPreferred(id),
    }),
    [activeId],
  );

  return { rowProps };
}
